import { Fragment, useCallback, useMemo } from 'react'
import { HiCheck } from 'react-icons/hi'
import { HiXMark } from 'react-icons/hi2'
import { FiMinusCircle } from 'react-icons/fi'
import { AiOutlineInfoCircle } from 'react-icons/ai'
import {
	SegmentedTab,
	SegmentedTabList,
	SegmentedTabs,
	Tooltip
} from '@clickguard/design-system'
import { AdSpendDropdown } from '../../../../../../components/ad-spend-dropdown'
import { featureComparisonData, PLAN_SPEND_LIMITS } from '../data/plan-features'
import type { PricePill, AdSpendValue, FeatureRow } from '../data/plan-features'
import { useBillingStore } from '../../../../../../store/billing-store/billing-store'
import { FeatureComparisonProps } from './types'
import GoogleLogo from '../../../../../../assets/img/google.svg'
import MetaLogo from '../../../../../../assets/img/meta.svg'
import BingLogo from '../../../../../../assets/img/microsoft.svg'
import { BillingFrequency } from '../payment-overview/types'

const BILLING_OPTIONS: { value: BillingFrequency; label: string }[] = [
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly' },
	{ value: 'annually', label: 'Annually' }
]

const PricePills = ({
	value,
	billingFrequency
}: {
	value: PricePill
	billingFrequency?: string
}) => {
	if (value.monthly === 'Custom quote') {
		return (
			<div className="flex flex-col items-center">
				<span
					className="inline-flex items-center px-2 font-inter font-medium text-[10px] leading-[20px] rounded-full border border-brand-tint-border"
					style={{
						background: 'var(--feature-price-pill-bg)',
						color: 'var(--feature-price-pill-text)'
					}}
				>
					Custom quote
				</span>
			</div>
		)
	}

	const frequency = billingFrequency || 'annually'
	const priceValue = value[frequency as keyof PricePill] || value.monthly

	return (
		<div className="flex flex-col items-center">
			<span
				className="inline-flex items-center px-2 font-inter font-medium text-[10px] leading-[20px] rounded-full border border-brand-tint-border"
				style={{
					background: 'var(--feature-price-pill-bg)',
					color: 'var(--feature-price-pill-text)'
				}}
			>
				{priceValue} /month
			</span>
		</div>
	)
}

const AdSpendCell = ({ value }: { value: AdSpendValue }) => {
	const isWarning = value.variant === 'warning'
	return (
		<div className="flex items-center gap-1.5 justify-center">
			{isWarning ? (
				<FiMinusCircle className="w-4 h-4 text-[#FABC04] shrink-0" />
			) : (
				<HiCheck className="w-4 h-4 text-[#17B26A] shrink-0" />
			)}
			<span
				className="font-inter font-medium text-[10px] leading-[20px] whitespace-nowrap"
				style={{ color: isWarning ? '#FABC04' : '#17B26A' }}
			>
				{value.text}
			</span>
		</div>
	)
}

const COLUMN_POSITIONS: Record<string, { left: string; width: string }> = {
	lite: { left: '31%', width: '20%' },
	standard: { left: '51%', width: '18%' },
	pro: { left: '69%', width: '15.5%' },
	custom: { left: '84.5%', width: '15.5%' }
}

export const FeatureComparison = ({
	adSpend,
	onAdSpendChange,
	recommendedPlanId = 'standard',
	selectedPlanId,
	billingFrequency,
	onBillingFrequencyChange
}: FeatureComparisonProps) => {
	const activePlanId = selectedPlanId || recommendedPlanId
	const highlightPos =
		COLUMN_POSITIONS[activePlanId] || COLUMN_POSITIONS.standard
	const billingStore = useBillingStore()

	const getPlanPrice = useCallback(
		(tier: string, period: 'monthly' | 'quarterly' | 'annually'): string => {
			const plan = billingStore.plans.find((p) => p.tier === tier)
			if (!plan) return '-'
			const intervalMap = {
				monthly: { interval: 'month', interval_count: 1 },
				quarterly: { interval: 'month', interval_count: 3 },
				annually: { interval: 'year', interval_count: 1 }
			}
			const { interval, interval_count } = intervalMap[period]
			const prices = plan.prices.filter(
				(p) => p.interval === interval && p.interval_count === interval_count
			)
			const spendAmount = adSpend * 1000
			let bestPrice = prices.find((p) => p.spend === spendAmount)
			if (!bestPrice) {
				const higher = prices
					.filter((p) => p.spend >= spendAmount)
					.sort((a, b) => a.spend - b.spend)
				bestPrice = higher[0] || prices[prices.length - 1]
			}
			if (!bestPrice) return '-'
			const monthlyVal =
				(bestPrice.pricing_by_currency?.usd?.price_monthly ??
					bestPrice.price_monthly ??
					bestPrice.price) / 100
			return `$${Math.round(monthlyVal)}`
		},
		[billingStore.plans, adSpend]
	)

	const getAdSpendValue = useCallback(
		(tier: string): AdSpendValue => {
			const limit = PLAN_SPEND_LIMITS[tier] ?? Infinity
			if (tier === 'custom')
				return { text: 'Custom ad spend', variant: 'success' }
			const covered = adSpend <= limit
			return {
				text: `Up to $${limit.toLocaleString()},000 ad spend`,
				variant: covered ? 'success' : 'warning'
			}
		},
		[adSpend]
	)

	const selectedFrequency = billingFrequency || 'annually'

	const pricePills = useMemo(() => ({
		lite: {
			label: 'Lite',
			monthly: getPlanPrice('lite', 'monthly'),
			quarterly: getPlanPrice('lite', 'quarterly'),
			annually: getPlanPrice('lite', 'annually')
		} as PricePill,
		standard: {
			label: 'Standard',
			monthly: getPlanPrice('standard', 'monthly'),
			quarterly: getPlanPrice('standard', 'quarterly'),
			annually: getPlanPrice('standard', 'annually')
		} as PricePill,
		pro: {
			label: 'Pro',
			monthly: getPlanPrice('pro', 'monthly'),
			quarterly: getPlanPrice('pro', 'quarterly'),
			annually: getPlanPrice('pro', 'annually')
		} as PricePill,
		custom: {
			label: 'Custom',
			monthly: 'Custom quote',
			quarterly: 'Custom quote',
			annually: 'Custom quote'
		} as PricePill
	}), [getPlanPrice])

	const dynamicRows: FeatureRow[] = useMemo(() => {
		return featureComparisonData
			.filter((row) => row.type !== 'price')
			.map((row) => {
				if (row.type === 'adSpend') {
					return {
						...row,
						lite: getAdSpendValue('lite'),
						standard: getAdSpendValue('standard'),
						pro: getAdSpendValue('pro'),
						custom: getAdSpendValue('custom')
					}
				}
				return row
			})
	}, [getAdSpendValue])

	const renderCell = (
		value: boolean | string | PricePill | AdSpendValue | 'all',
		type?: string
	) => {
		if (type === 'price') {
			return (
				<PricePills
					value={value as PricePill}
					billingFrequency={selectedFrequency}
				/>
			)
		}
		if (type === 'adSpend') {
			return <AdSpendCell value={value as AdSpendValue} />
		}
		if (type === 'platforms') {
			return (
				<div className="flex items-center gap-2 justify-center">
					<img src={GoogleLogo} alt="Google Ads" className="w-4 h-4" />
					<img src={MetaLogo} alt="Meta Ads" className="w-4 h-4" />
					<img src={BingLogo} alt="Bing Ads" className="w-4 h-4" />
				</div>
			)
		}
		if (typeof value === 'string') {
			return (
				<span className="font-medium text-[10px] leading-[20px] text-t-desc-gray">
					{value}
				</span>
			)
		}
		if (value) {
			return <HiCheck className="w-5 h-5 text-[#17B26A] mx-auto" />
		}
		return <HiXMark className="w-5 h-5 text-t-gray-hold mx-auto" />
	}

	return (
		<div>
			{/* Title */}
			<h3 className="font-grotesk font-semibold text-[20px] leading-[26px] text-t-google-ad">
				Compare all plans
			</h3>
			<div className="flex items-center self-stretch h-px bg-t-checkbox my-6" />
			<div className="relative mb-8">
				{/* Selected plan column highlight overlay */}
				<div
					className="absolute top-0 bottom-0 border-2 border-[rgba(143,136,255,0.25)] rounded-[18px] pointer-events-none z-10"
					style={{ left: highlightPos.left, width: highlightPos.width }}
				/>
				<div
					className="absolute top-0 bottom-0 border border-[#8F88FF] rounded-[18px] pointer-events-none z-10"
					style={{ left: highlightPos.left, width: highlightPos.width }}
				/>

				{/* Recommended badge */}
				<div
					className="absolute flex items-center gap-1 z-20"
					style={{
						left: (COLUMN_POSITIONS[recommendedPlanId] || COLUMN_POSITIONS.standard).left,
						width: (COLUMN_POSITIONS[recommendedPlanId] || COLUMN_POSITIONS.standard).width,
						top: '-6px'
					}}
				>
					<div className="flex items-center gap-1 mx-auto">
						<span className="inline-flex items-center px-2 h-[17px] rounded-full border border-b-yellow-tint-border font-grotesk font-medium text-[9px] leading-[20px] text-t-default whitespace-nowrap bg-b-yellow-tint-2">
							Recommended for you
						</span>
					</div>
				</div>

				<div className="flex flex-col">
					{/* Header row with ad spend dropdown */}
					<div className="bg-[var(--fc-band,#F5F5F5)] rounded-[6px] flex items-end min-h-[70px]">
						<div className="w-[31%] shrink-0 pl-3.5 pb-3 pt-3 flex items-center gap-4">
							<p className="font-grotesk font-medium text-[14px] leading-[16px] text-t-google-ad shrink-0">
								Monthly
								<br />
								ad spend
							</p>
							<div className="flex-1 min-w-0 pr-2">
								<AdSpendDropdown
									value={adSpend}
									onChange={onAdSpendChange}
									showCustomInput={false}
								/>
							</div>
						</div>
						<div className="w-[20%] shrink-0 text-center pb-[26px]">
							<span className={`font-grotesk text-[14px] leading-[18px] ${activePlanId === 'lite' ? 'font-semibold text-brand-tint-text' : 'font-medium text-t-gray-checkout'}`}>
								Lite
							</span>
						</div>
						<div className="w-[18%] shrink-0 text-center pb-[26px]">
							<span className={`font-grotesk text-[14px] leading-[18px] ${activePlanId === 'standard' ? 'font-semibold text-brand-tint-text' : 'font-medium text-t-desc-gray'}`}>
								Standard
							</span>
						</div>
						<div className="w-[15.5%] shrink-0 text-center pb-[26px]">
							<span className={`font-grotesk text-[14px] leading-[18px] ${activePlanId === 'pro' ? 'font-semibold text-brand-tint-text' : 'font-medium text-t-gray-checkout'}`}>
								Pro
							</span>
						</div>
						<div className="w-[15.5%] shrink-0 text-center pb-[26px]">
							<span className={`font-grotesk text-[14px] leading-[18px] ${activePlanId === 'custom' ? 'font-semibold text-brand-tint-text' : 'font-medium text-t-gray-checkout'}`}>
								Custom
							</span>
						</div>
					</div>

					{/* Billing frequency toggle row */}
					<div className="h-px bg-t-traffic" />
					<div className="flex items-center h-[70px]">
						<div className="w-[31%] shrink-0 px-1.5">
							<div className="relative">
								{/* The same DS billing switch as the Plan overview card. */}
								<SegmentedTabs
									value={selectedFrequency}
									onChange={(v) =>
										onBillingFrequencyChange?.(v as BillingFrequency)
									}
									size="small"
									activeTone="brand"
									aria-label="Billing frequency"
								>
									<SegmentedTabList className="w-full">
										{BILLING_OPTIONS.map((option) => (
											<SegmentedTab
												key={option.value}
												value={option.value}
												label={option.label}
												className="flex-1 justify-center"
											/>
										))}
									</SegmentedTabList>
								</SegmentedTabs>
								{/* Save badges */}
								<div className="flex absolute top-[calc(100%-6px)] left-0 right-0 pointer-events-none">
									<div className="flex-1" />
									<div className="flex-1 flex justify-center">
										<span className="bg-b-yellow-tint-2 border border-b-yellow-tint-border text-t-default text-[9px] font-grotesk font-medium px-2 rounded-full leading-[20px] whitespace-nowrap">
											save 10%
										</span>
									</div>
									<div className="flex-1 flex justify-center">
										<span className="bg-b-yellow-tint-2 border border-b-yellow-tint-border text-t-default text-[9px] font-grotesk font-medium px-2 rounded-full leading-[20px] whitespace-nowrap">
											save 20%
										</span>
									</div>
								</div>
							</div>
						</div>
						<div className="w-[20%] shrink-0 flex justify-center">
							<PricePills value={pricePills.lite} billingFrequency={selectedFrequency} />
						</div>
						<div className="w-[18%] shrink-0 flex justify-center">
							<PricePills value={pricePills.standard} billingFrequency={selectedFrequency} />
						</div>
						<div className="w-[15.5%] shrink-0 flex justify-center">
							<PricePills value={pricePills.pro} billingFrequency={selectedFrequency} />
						</div>
						<div className="w-[15.5%] shrink-0 flex justify-center">
							<PricePills value={pricePills.custom} billingFrequency={selectedFrequency} />
						</div>
					</div>

					{/* Data rows */}
					{dynamicRows.map((row, idx) => (
						<Fragment key={idx}>
							<div className="h-px bg-t-traffic" />
							<div className={`flex items-center px-[6px] py-3 ${idx % 2 === 0 ? 'bg-[var(--fc-zebra,#F9F9FB)]' : ''} rounded-[6px]`}>
								<div className="w-[31%] shrink-0 flex items-center gap-1">
									<span className="font-grotesk font-medium text-[14px] leading-[18px] text-t-gray-checkout">
										{row.name}
									</span>
									{row.tooltip && (
										<Tooltip
											content={row.tooltip}
											position="top"
											portal
											zIndex={10000005}
											maxWidth={270}
										>
											<AiOutlineInfoCircle className="w-4 h-4 text-[#8F88FF] shrink-0" />
										</Tooltip>
									)}
								</div>
								<div className="w-[20%] shrink-0 flex justify-center">
									{renderCell(row.lite, row.type)}
								</div>
								<div className="w-[18%] shrink-0 flex justify-center">
									{renderCell(row.standard, row.type)}
								</div>
								<div className="w-[15.5%] shrink-0 flex justify-center">
									{renderCell(row.pro, row.type)}
								</div>
								<div className="w-[15.5%] shrink-0 flex justify-center">
									{renderCell(row.custom, row.type)}
								</div>
							</div>
						</Fragment>
					))}
				</div>
			</div>
		</div>
	)
}
